import type { Survey } from 'types';
import { toast } from 'sonner';
import { readApiError } from '@/state/billing/api-error';
import { useSurvey } from '@/state/surveys/useSurvey';
import { useSentimentEstimate } from '@/state/surveys/useSentimentEstimate';
import { formatUnits } from '@/state/billing/billing-format';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';

type SentimentSettingsProps = { survey: Survey };

/**
 * The one editable setting on the survey's read-only details screen: it changes what the survey
 * costs, so it has to be reachable after the survey is running, not only in the builder. Off by
 * default; turning it on classifies each new free-text comment as positive, neutral or negative
 * and spends AI credits, so the page says roughly how many, and offers the comments collected
 * earlier as a separate, confirmed action.
 */
export const SentimentSettings = ({ survey }: SentimentSettingsProps) => {
  const { updateSurvey, isSaving } = useSurvey(survey.id);
  const { estimate, isConfirming, openConfirm, closeConfirm, analyseExisting } = useSentimentEstimate({
    surveyId: survey.id,
  });

  return (
    <div className="flex flex-col items-end gap-1.5">
      <label className="flex items-center justify-end gap-2">
        <span className="supporting">{survey.sentimentEnabled ? 'On' : 'Off'}</span>
        <Switch
          checked={survey.sentimentEnabled}
          disabled={isSaving}
          onCheckedChange={(checked) => {
            updateSurvey(survey.id, { sentimentEnabled: checked }).catch((error: unknown) => {
              toast.error('Could not save the setting', { description: readApiError(error).message ?? undefined });
            });
          }}
          aria-label="Classify the sentiment of comments. Uses AI credits."
        />
      </label>

      {estimate && (
        <p className="supporting m-0 text-right">
          {estimate.monthlyComments > 0
            ? `About ${formatUnits(estimate.monthlyCredits)} AI credits a month at the current pace (${formatUnits(estimate.monthlyComments)} comments in the last 30 days). An estimate.`
            : 'Uses a small number of AI credits per comment. No comments yet to estimate from.'}
        </p>
      )}

      {survey.sentimentEnabled && estimate && estimate.backlogComments > 0 && (
        <>
          <Button variant="outline" size="sm" className="h-8 rounded-lg" onClick={openConfirm}>
            Analyse existing responses (about {formatUnits(estimate.backlogCredits)} credits)
          </Button>
          <ConfirmDialog
            open={isConfirming}
            title="Analyse existing responses"
            description={`${formatUnits(estimate.backlogComments)} earlier comments will be classified, using about ${formatUnits(estimate.backlogCredits)} AI credits. They are worked through a few hundred at a time, and stop if your credits run out.`}
            confirmLabel="Analyse"
            onConfirm={analyseExisting}
            onCancel={closeConfirm}
          />
        </>
      )}
    </div>
  );
};
